/**
 * LogQL presets + the Grafana Explore deep-link builder for Pane 2.
 *
 * Pure functions, no React -- so the query-construction logic (the
 * part that is actually easy to get subtly wrong) is unit-testable
 * without rendering anything, matching this repo's habit of splitting
 * pure logic out of I/O-bound wrappers (`_parse_runbook_entry`,
 * `_run_steps`).
 *
 * Label vocabulary is NOT invented here, and it must satisfy TWO
 * collectors, not one. `docker/alloy/config.alloy` (Compose) emits
 * `job="docker"`, `container` = the Docker container_name, and
 * `compose_service`. nexsoss-deploy's `k8s/platform/observability.yaml`
 * (Kubernetes) emits `job="loki.source.kubernetes.pods"`, `container` =
 * the pod's SHORT container name, plus `namespace` and `pod`. A preset
 * naming a label or value only one of them sets silently returns zero
 * lines, which is worse than no preset at all -- and that is not
 * hypothetical: every preset here selected `job="docker"` and returned
 * nothing at all under Kubernetes until 2026-08-13.
 *
 * `{site=~".+"}` is the portable selector. `site` is the one label BOTH
 * collectors set on every stream, and it is the same label each one
 * routes the tenant by, so within a tenant it is always present.
 *
 * That is NOT the client choosing its own scope, and it is not a
 * loosening of 3a's security gate: `=~".+"` names no particular site.
 * The api-server still derives the Loki tenant from the validated
 * `site` parameter and sends it as `X-Scope-OrgID`, so the query is
 * already confined to one site's stream; a matcher that merely asserts
 * "the label is non-empty" can narrow that, never widen it. What the
 * old comment here forbade -- writing `site="<a site name>"` into a
 * preset -- is still forbidden, and still absent.
 */

export interface LogPreset {
  label: string;
  logql: string;
}

/** Service SUBSTRINGS worth a one-click preset -- not container names.
 *
 *  Mirrors the core-infra idea in `loki_client.py::_core_infra_containers`
 *  without importing it (a frontend cannot), kept short enough to stay
 *  honest rather than attempting to enumerate every service.
 *
 *  Substrings, and wrapped in `.*` below, because a LogQL label regex is
 *  FULLY ANCHORED and the same service is labelled differently by each
 *  collector: the Zenoh router is `router` under Kubernetes and
 *  `zenoh-router-<site>` under Compose (the sibling repo templates every
 *  core `container_name` with `${SITE_ID}`). So `container=~"zenoh-router"`
 *  matches neither, and `container=~"router|sim"` matches Kubernetes only.
 *  The stems below are the longest fragment common to both spellings. */
const CORE_RMF_SERVICES = [
  'task-dispatcher',
  'traffic-schedule',
  'visualizer',
  'trajectory-publisher',
  'router',
];

/** LogQL string-literal escaping: backslashes first, then the quote. */
export function escapeLogql(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** RE2 metacharacter escaping, for a value interpolated into a line
 *  filter's PATTERN rather than into a plain string.
 *
 *  Both layers are needed and the order matters -- regex first, then
 *  string -- because a double-quoted LogQL string is parsed as a Go
 *  string literal BEFORE its bytes reach RE2. A lone `\.` is a valid
 *  regex but not a valid Go escape, and Loki answers `parse error ...
 *  invalid char escape` with HTTP 400. Deliberately does NOT escape
 *  `-`: RE2 rejects an escaped hyphen outside a character class, and
 *  every task id is hyphenated (see `_logql_escape` in
 *  `loki_client.py`, which had to learn the same thing the hard way). */
export function escapeLogqlRegex(value: string): string {
  return escapeLogql(value.replace(/([.^$*+?()[\]{}|\\])/g, '\\$1'));
}

export function logPresetsForIssue(issue: {
  title: string;
  source: string;
  site: string | null;
}): LogPreset[] {
  // Two exclusions are part of the errorish filter, and both are
  // load-bearing. Severity is inferred by SUBSTRING here (the emitters
  // share no severity encoding -- `[ERROR]`, bare ` WARN `, ` WARNING `,
  // `level=error`, and structlog JSON with no level field at all), so:
  //   !~ "level=(info|debug)"  drops lines that declare themselves INFO.
  //     Without it Loki's own query log -- which is level=info and
  //     quotes the query text, and that text contains
  //     error|warn|timeout|fail|exception -- is counted as errors. That
  //     was 67% of the platform tenant's apparent errors when measured.
  //   !~ "^[ \t]"  drops indented lines: Python traceback CONTEXT
  //     FRAMES, i.e. echoed source code. `with urllib.request.urlopen(
  //     req, timeout=5.0)` was being shown to operators as a timeout
  //     error because the SOURCE contains the word.
  // Do NOT replace this with a pure severity match: the lifecycle
  // broker's JSON carries no level, and it is the largest real error
  // class in the cluster.
  const errorish =
    '|~ "(?i)error|warn|timeout|fail|exception" !~ "level=(info|debug)" !~ "^[ \\t]"';
  const anySite = '{site=~".+"}';
  const services = `.*(${CORE_RMF_SERVICES.join('|')}).*`;
  const presets: LogPreset[] = [
    {
      label: 'Core RMF errors',
      logql: `{site=~".+", container=~"${services}"} ${errorish}`,
    },
    { label: 'All containers, errors only', logql: `${anySite} ${errorish}` },
  ];

  // A task-shaped issue usually carries the task id in its title; a
  // free-text grep for it across every container is exactly the
  // "which service mentioned this id" question an engineer asks
  // first, and is the same technique loki_client.py's
  // `_extract_search_token` uses server-side for the AI prefetch.
  const taskId = issue.title.match(/\b[a-z]+\.dispatch-[0-9a-f]+\b/i)?.[0];
  if (taskId) {
    presets.unshift({
      label: `Trace task ${taskId}`,
      logql: `${anySite} |~ "(?i)${escapeLogqlRegex(taskId)}"`,
    });
  }

  return presets;
}

/**
 * Grafana Explore deep-link. Uses the per-site datasource uid
 * convention (`loki-{site}`) provisioned in
 * `docker/grafana/provisioning/datasources/loki.yaml` -- the tenant is
 * bound server-side inside Grafana's own datasource config, so this
 * URL selects a DATASOURCE, never a tenant header. That keeps the
 * "the client never chooses its own tenant" rule intact across the
 * hand-off to Grafana.
 *
 * Window: centred on the issue's last occurrence, ±30 min, so the
 * deep-link lands on the incident rather than on "now" (which, for an
 * issue last seen hours ago, would show nothing and read as a broken
 * link).
 */
export function grafanaExploreUrl(
  grafanaBaseUrl: string,
  site: string,
  logql: string,
  centreMs: number,
  windowMs = 30 * 60 * 1000,
): string {
  const pane = {
    datasource: `loki-${site}`,
    queries: [{ refId: 'A', expr: logql, datasource: { type: 'loki', uid: `loki-${site}` } }],
    range: { from: String(centreMs - windowMs), to: String(centreMs + windowMs) },
  };
  const params = new URLSearchParams({ schemaVersion: '1', panes: JSON.stringify({ support: pane }), orgId: '1' });
  return `${grafanaBaseUrl.replace(/\/$/, '')}/explore?${params.toString()}`;
}
